import { Component, computed, inject, viewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { IWizardStepComponent } from '@mwe/models';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { UiComponentsModule } from '@mwe/ui/components';
import { ContractPartnerTypeComponent } from './components/contract-partner-type/contract-partner-type.component';
import { ContactInformationComponent } from './components/contact-information/contact-information.component';
import { InvoicingInformationComponent } from './components/invoicing-information/invoicing-information.component';
import { AlternativeInvoicingComponent } from './components/alternative-invoicing/alternative-invoicing.component';
import { ContractPartnerEmptyComponent } from './components/contract-partner-empty/contract-partner-empty.component';
import { MeterPointChangeSignalState } from '../meterpoint-change-request.types';
import { checkPrevDataAvailable, closeIfInactive } from '../meter-point-change-request.utils';
import { MeterPointChangeType } from '../meterpoint-change-request.types';
import { ROUTE_CURRENT_CONTRACT_PARTNER, ROUTE_METER_POINT_CHANGE_REQUEST } from '../../../app.constants';
import { Router } from '@angular/router';

@Component({
  selector: 'ikp-new-contract-partner',
  imports: [
    UiComponentsModule,
    AlternativeInvoicingComponent,
    ContractPartnerTypeComponent,
    ContactInformationComponent,
    InvoicingInformationComponent,
    FormsModule,
    ContractPartnerEmptyComponent,
  ],
  templateUrl: './new-contract-partner.component.html',
  standalone: true,
})
export class NewContractPartnerComponent implements IWizardStepComponent {
  protected router = inject(Router);
  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);
  private alternativeInvoicingForm = viewChild(AlternativeInvoicingComponent);
  private contactInformationForm = viewChild(ContactInformationComponent);
  private contractPartnerEmptyForm = viewChild(ContractPartnerEmptyComponent);

  protected readonly hasPrevStepData = computed(
    () => this.wizardStore.state().productAndDate.meterPoints.length > 0 && this.wizardStore.state().meterPoint !== null,
  );
  protected readonly changeType = computed<string>(() => this.wizardStore.state().stepChangeType);
  protected readonly emptyContractPartner = computed(() => {
    const currentPartner = this.wizardStore.state().currentContractPartner;
    return currentPartner?.emptyContractPartner || currentPartner?.occupiedContractPartner;
  });

  hasAllNeededData(): boolean {
    closeIfInactive(this.wizardStore.state(), this.router);
    checkPrevDataAvailable(this.hasPrevStepData(), this.router, [ROUTE_METER_POINT_CHANGE_REQUEST, ROUTE_CURRENT_CONTRACT_PARTNER]);
    return true;
  }

  submit(): Promise<void> {
    return Promise.resolve(undefined);
  }

  validate(): boolean {
    if (this.changeType() === MeterPointChangeType.OCCUPIED_TO_EMPTY) {
      return this.contractPartnerEmptyForm()?.validForSubmission ?? true;
    }
    const isContactInformationFormValid = this.isContactInformationFormValid();
    const isInvoicingInformationFormValid = this.isInvoicingInformationFormValid();
    return isContactInformationFormValid && isInvoicingInformationFormValid;
  }

  private isContactInformationFormValid(): boolean {
    return this.contactInformationForm().validContactInformationForm;
  }

  private isInvoicingInformationFormValid(): boolean {
    if (this.wizardStore.state().newContractPartner.alternativeInvoicingAddress) {
      return this.alternativeInvoicingForm().validForSubmission;
    }
    return true;
  }

  protected readonly MeterPointChangeType = MeterPointChangeType;
}
